import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { UserPlus, ArrowLeft, Mail, Lock, User, Phone, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Registrasi gagal. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Register() {
  const { user, emailRegister } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const next = new URLSearchParams(location.search).get("next") || "";

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (user && !done) {
      if (next && next.startsWith("/")) navigate(next, { replace: true });
      else navigate("/my-bookings", { replace: true });
    }
  }, [user, next, navigate, done]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Konfirmasi password tidak cocok."); return; }
    if (password.length < 8) { setErr("Password minimal 8 karakter."); return; }
    if (!agreed) { setErr("Anda harus menyetujui Syarat & Ketentuan."); return; }
    setBusy(true);
    try {
      await emailRegister({ name: name.trim(), email: email.trim().toLowerCase(), phone: phone.trim(), password });
      setDone(true);
      toast.success("Registrasi berhasil. Cek email untuk verifikasi.");
    } catch (e2) {
      const msg = formatErr(e2?.response?.data?.detail);
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-muted/40">
        <Helmet><title>Registrasi Berhasil — BATIK</title></Helmet>
        <div className="w-full max-w-md bg-card border border-border rounded-lg p-10 shadow-xl text-center" data-testid="register-success">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-serif text-2xl text-primary mb-3">Registrasi Berhasil!</h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Kami telah mengirim <b>email verifikasi</b> ke <span className="text-primary font-medium">{email}</span>. Silakan cek inbox (dan folder spam) untuk mengaktifkan akun Anda.
          </p>
          <p className="text-xs text-muted-foreground mb-6 italic">Anda tetap bisa akses dashboard, namun beberapa fitur akan aktif setelah email diverifikasi.</p>
          <button onClick={() => navigate("/my-bookings")} className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90" data-testid="go-dashboard-btn">
            Ke Dashboard Saya
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/40" data-testid="register-page">
      <Helmet><title>Daftar — BATIK Tour & Travel</title></Helmet>
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 lg:p-10 shadow-xl my-8">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-xs mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke website
        </Link>

        <div className="h-12 w-12 rounded-full bg-accent/15 flex items-center justify-center mb-5">
          <UserPlus className="h-5 w-5 text-accent" />
        </div>

        <p className="overline mb-2">Daftar Portal Jamaah</p>
        <h1 className="font-serif text-3xl text-primary tracking-tight leading-tight mb-2">
          Buat Akun Baru
        </h1>
        <p className="text-sm text-muted-foreground mb-6">Nama harus sesuai dengan paspor Anda.</p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <Field label="Nama Lengkap (sesuai paspor)" icon={User} testid="reg-name" value={name} onChange={setName} required minLength={2} placeholder="Muhammad Ahmad Rasyid" />
          <Field label="Email" icon={Mail} testid="reg-email" type="email" value={email} onChange={setEmail} required placeholder="anda@email.com" autoComplete="email" />
          <Field label="Nomor HP / WhatsApp" icon={Phone} testid="reg-phone" type="tel" value={phone} onChange={setPhone} required minLength={8} placeholder="08123456xxxx" autoComplete="tel" />

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password</label>
            <div className="mt-1.5 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input data-testid="reg-password" type={showPw ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-10 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Minimal 8 karakter" autoComplete="new-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Field label="Konfirmasi Password" icon={Lock} testid="reg-confirm" type="password" value={confirm} onChange={setConfirm} required minLength={8} placeholder="Ulangi password" autoComplete="new-password" />

          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="reg-agree" className="mt-0.5 accent-accent" />
            <span>Saya menyetujui <Link to="/about" className="text-accent underline">Syarat &amp; Ketentuan</Link> dan Kebijakan Privasi BATIK Tour &amp; Travel.</span>
          </label>

          {err && <p className="text-xs text-red-600" data-testid="register-error">{err}</p>}

          <button type="submit" disabled={busy} data-testid="register-submit" className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Daftar Sekarang
          </button>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Sudah punya akun?{" "}
          <Link to="/login" className="text-accent font-semibold hover:underline" data-testid="to-login-link">Masuk di sini</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, testid, type = "text", value, onChange, required, minLength, placeholder, autoComplete }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</label>
      <div className="mt-1.5 relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input data-testid={testid} type={type} required={required} minLength={minLength} value={value} onChange={(e) => onChange(e.target.value)} className="w-full pl-10 pr-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder={placeholder} autoComplete={autoComplete} />
      </div>
    </div>
  );
}
